'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import './styles.css'
import brandLogo from '/vod-logo.svg?url'
import { Video, VideoPlayer } from '@videojs/react/video'
import { Container } from '@videojs/react'
import { CalendarDays, LoaderCircle, Play } from 'lucide-react'
import { fetchJson } from './api'
import { defaultCamera, playerCameras, presetCameras, setPresetForUrl } from './cameras'
import {
  dayPlayhead,
  fetchCameraCalendar,
  fetchCameraDay,
  fetchCameraEvents,
} from './playerArchive'
import type { Camera } from './types'
import {
  addDays,
  buildSegments,
  dateLabel,
  dayBounds,
  localDay,
  monthOf,
  onDay,
  wallToMedia,
} from './time'
import { useArchivePlayer } from './useArchivePlayer'
import { usePlayerFullscreen } from './usePlayerFullscreen'
import { PlayerFooter } from './PlayerFooter'
import { ArchiveNavigation } from './ArchiveNavigation'
import { CropOverlay } from './CropOverlay'
import { AudioVisualization, type AudioVisualizationMode } from './AudioVisualization'
import { Captions } from './Captions'
import { speechRegions, speechTarget } from './voice'
import { rangeFirst } from './timelineExperiment'
import { projectTimezone, setPlayerPosition } from './playerUrl'

export type Controls = 'full' | 'minimal' | 'none'
export type TimelineZoom = 'day' | '6h' | '1h' | '10m'
export type Position = { camera: string; day: string; at: string }
export interface VodPlayerProps {
  /** Initial selection. Remount with a new key to reset it. */
  camera?: string
  day?: string
  at?: string
  /** Play only ready cloud archives, skipping recordings without one. */
  archiveOnly?: boolean
  /** Logical location IDs to exclude from camera selection. */
  excludeLocations?: string[]
  /** Named viewer preset; omitted/unknown names use the configured default. */
  preset?: string
  controls?: Controls | boolean
  defaultZoom?: TimelineZoom
  /** Whether archive controls are expanded on initial load. */
  defaultExpanded?: boolean
  /** Open with the hour ending at the latest recording. */
  latestHour?: boolean
  /** Initial visible wall-clock range; does not trim or stop playback. */
  rangeStart?: string
  rangeEnd?: string
  apiBaseUrl?: string
  keyboard?: 'focused' | 'global' | 'none'
  className?: string
  onPositionChange?: (position: Position) => void
  /** Show the logo linking to the current position on vod.ohn.sh, regardless of controls. */
  showLogo?: boolean
  eventsEndpoint?: string
  eventsRevision?: number
  /** Show the fading one-second peak envelope on audio waveforms. Defaults to true. */
  amplitudeEnvelope?: boolean
  /** Live audio display. Defaults to the time-domain waveform. */
  visualization?: AudioVisualizationMode
  /** Deterministic screenshot layout and exact media target for the capture route. */
  captureMode?: boolean
}

export function VodPlayer(props: VodPlayerProps) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false, gcTime: 5 * 60000 },
        },
      }),
  )
  return (
    <QueryClientProvider client={client}>
      <ArchivePlayer {...props} />
    </QueryClientProvider>
  )
}

const mobileLandscapeQuery =
  '(max-width: 1000px) and (max-height: 600px) and (orientation: landscape)'
const captionsStorageKey = 'vod:captions'
function requestedDay(value = '') {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return ''
  try {
    dayBounds(value, 'UTC')
    return value
  } catch {
    return ''
  }
}

function ArchivePlayer({
  camera: initialCamera = '',
  day: initialDay,
  at,
  archiveOnly = false,
  excludeLocations,
  preset,
  controls: controlsProp = 'full',
  defaultZoom = 'day',
  latestHour = false,
  defaultExpanded = false,
  rangeStart,
  rangeEnd,
  apiBaseUrl = '',
  keyboard = 'focused',
  className = '',
  onPositionChange,
  showLogo = true,
  eventsEndpoint,
  eventsRevision = 0,
  amplitudeEnvelope = true,
  visualization = 'waveform',
  captureMode = false,
}: VodPlayerProps) {
  const [initialAt] = useState(() =>
    Date.parse(at ?? (rangeFirst ? rangeStart : undefined) ?? ''),
  )
  const controls =
    controlsProp === false ? 'none' : controlsProp === true ? 'full' : controlsProp
  const [cameraOpen, setCameraOpen] = useState(false)
  const [mobilePickers, setMobilePickers] = useState(false)
  const apiBase = apiBaseUrl.replace(/\/$/, '')
  const cameras = useQuery({
    queryKey: [apiBase, 'cameras'],
    queryFn: async () =>
      playerCameras(await fetchJson<Camera[]>(`${apiBase}/api/cameras`)),
    staleTime: 30000,
  })
  const visibleCameras = useMemo(
    () =>
      cameras.data &&
      presetCameras(cameras.data, preset).filter(
        (camera) => !excludeLocations?.includes(camera.id),
      ),
    [cameras.data, preset, excludeLocations],
  )
  const [cameraId, setCameraId] = useState(initialCamera)
  const needsResolution =
    !!initialCamera &&
    !!cameras.data?.length &&
    !cameras.data.some((camera) => camera.id === initialCamera)
  const resolvedCamera = useQuery({
    queryKey: [apiBase, 'resolve-camera', initialCamera, initialAt, initialDay],
    enabled: needsResolution,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (Number.isFinite(initialAt)) params.set('at', String(initialAt))
      else if (initialDay) params.set('date', initialDay)
      return fetchJson<{ cameraId: string }>(
        `${apiBase}/api/cameras/${encodeURIComponent(initialCamera)}/resolve?${params}`,
        signal,
      )
    },
    retry: false,
  })
  const camera = visibleCameras?.find((c) => c.id === cameraId)
  const [day, setDay] = useState(() => requestedDay(initialDay))
  const eventQuery = useQuery({
    queryKey: [
      apiBase,
      'events',
      eventsEndpoint,
      eventsRevision,
      cameraId,
      day,
      camera?.sourceIds,
    ],
    enabled: !!camera && !!day,
    staleTime: 30000,
    queryFn: ({ signal }) =>
      fetchCameraEvents(eventsEndpoint ?? `${apiBase}/api/events`, camera!, day, signal),
  })
  const [clock, setClock] = useState(Number.isFinite(initialAt) ? initialAt : 0)
  const clockRef = useRef(clock)
  clockRef.current = clock
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarMonth, setCalendarMonth] = useState('')
  const [notice, setNotice] = useState('')
  const [hud, setHud] = useState(true)
  const [toolsExpanded, setToolsExpanded] = useState(() => {
    if (captureMode) return defaultExpanded
    try {
      const stored = localStorage.getItem('vod:tools-expanded')
      if (stored === 'true' || stored === 'false') return stored === 'true'
    } catch {
      // Storage may be unavailable in embedded players.
    }
    return defaultExpanded
  })
  useEffect(() => {
    if (captureMode) return
    try {
      localStorage.setItem('vod:tools-expanded', String(toolsExpanded))
    } catch {
      // The disclosure still works when storage is unavailable.
    }
  }, [toolsExpanded, captureMode])
  const toolsExpandedRef = useRef(defaultExpanded)
  toolsExpandedRef.current = toolsExpanded
  const dockHovered = useRef(false)
  const initialRange = useMemo(() => {
    const start = Date.parse(rangeStart ?? '')
    const end = Date.parse(rangeEnd ?? '')
    return rangeFirst && Number.isFinite(start) && Number.isFinite(end) && end > start
      ? { start, end }
      : undefined
  }, [rangeStart, rangeEnd])
  useEffect(() => {
    const query = matchMedia('(pointer: coarse) and (max-width: 1000px)')
    const update = () => setMobilePickers(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  const [mobileLandscape, setMobileLandscape] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia(mobileLandscapeQuery).matches,
  )
  const hudTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activePointers = useRef(new Set<number>())
  const wakeGesture = useRef(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const element = root.current
    const dock = element?.querySelector('.bottom-dock')
    if (!element || !dock) return
    const observer = new ResizeObserver(() => {
      element.style.setProperty(
        '--caption-dock-height',
        `${dock.getBoundingClientRect().height}px`,
      )
    })
    observer.observe(dock)
    return () => observer.disconnect()
  }, [controls])
  const {
    expanded,
    viewportFullscreen,
    toggle: toggleFullscreen,
  } = usePlayerFullscreen(root)
  const pending = useRef<{
    camera: string
    day: string
    utc: number | null
    autoplay: boolean
  } | null>(null)
  const initialized = useRef(false)
  const clipNavigation = useRef<AbortController | null>(null)
  const [loadingPrevious, setLoadingPrevious] = useState(false)
  const timezone = camera?.timezone ?? projectTimezone
  const today = localDay(Date.now(), timezone)
  const dayRef = useRef(day)
  dayRef.current = day
  const timezoneRef = useRef(timezone)
  timezoneRef.current = timezone

  useEffect(() => {
    if (!visibleCameras?.length || initialized.current) return
    if (needsResolution && resolvedCamera.isPending) return
    initialized.current = true
    const selected =
      visibleCameras.find((c) => c.id === (resolvedCamera.data?.cameraId ?? cameraId)) ??
      defaultCamera(visibleCameras)!
    if (selected.id !== cameraId) setCameraId(selected.id)
    const utc = Number.isFinite(initialAt)
      ? initialAt
      : (selected.latestReady ?? selected.first)
    const requested = day || localDay(utc, selected.timezone)
    const currentDay = localDay(Date.now(), selected.timezone)
    const targetDay = requested > currentDay ? currentDay : requested
    if (day !== targetDay) {
      setDay(targetDay)
      setCalendarMonth(monthOf(targetDay))
    }
    if (!pending.current)
      pending.current = {
        camera: selected.id,
        day: targetDay,
        utc: requested === targetDay && (Number.isFinite(initialAt) || !day) ? utc : null,
        autoplay: false,
      }
  }, [visibleCameras, needsResolution, resolvedCamera.data, resolvedCamera.isPending])

  const dayQuery = useQuery({
    queryKey: [apiBase, 'day', cameraId, day, camera?.sourceIds, archiveOnly],
    enabled: Boolean(camera && day),
    staleTime: 30000,
    queryFn: ({ signal }) => fetchCameraDay(apiBase, camera!, day, signal, archiveOnly),
  })
  const latestRange = useRef<{ start: number; end: number } | undefined>(undefined)
  if (latestHour && !latestRange.current && dayQuery.data?.recordings.length) {
    const end = Math.max(...dayQuery.data.recordings.map((recording) => recording.end))
    latestRange.current = { start: end - 3600000, end }
  }
  const calendar = useQuery({
    queryKey: [
      apiBase,
      'calendar',
      cameraId,
      calendarMonth || monthOf(day),
      camera?.sourceIds,
    ],
    enabled: Boolean(camera && day),
    staleTime: 30000,
    queryFn: ({ signal }) =>
      fetchCameraCalendar(apiBase, camera!, calendarMonth || monthOf(day), signal),
  })
  const segments = useMemo(
    () =>
      dayQuery.data
        ? buildSegments(
            [...dayQuery.data.recordings, ...dayQuery.data.followingRecordings],
            camera?.sourceIds,
          )
        : [],
    [dayQuery.data, camera?.sourceIds],
  )
  const audioSegments = useMemo(
    () =>
      dayQuery.data?.audioRecordings?.length
        ? buildSegments(
            [
              ...dayQuery.data.audioRecordings,
              ...(dayQuery.data.followingAudioRecordings ?? []),
            ],
            camera?.audioSourceIds,
          )
        : [],
    [dayQuery.data, camera?.audioSourceIds],
  )
  const dayStart = dayQuery.data
    ? Math.max(
        dayQuery.data.start,
        segments.find(
          (segment) =>
            segment.end > dayQuery.data!.start && segment.start < dayQuery.data!.end,
        )?.start ?? dayQuery.data.start,
      )
    : 0
  const [speechNavigationEnabled, setSpeechNavigationEnabled] = useState(false)
  const [captions, setCaptions] = useState(false)
  useEffect(() => {
    if (captureMode) return
    try {
      setCaptions(localStorage.getItem(captionsStorageKey) === 'true')
    } catch {
      // Storage may be unavailable in embedded players.
    }
  }, [])
  function toggleCaptions() {
    const enabled = !captions
    setCaptions(enabled)
    try {
      localStorage.setItem(captionsStorageKey, String(enabled))
    } catch {
      // Keep the toggle usable even when the preference cannot be saved.
    }
  }
  const speech = useMemo(
    () =>
      dayQuery.data
        ? speechRegions(segments, dayQuery.data.start, dayQuery.data.end, audioSegments)
        : [],
    [segments, audioSegments, dayQuery.data],
  )
  const navigateSpeech = speechNavigationEnabled && speech.length > 0
  const previousSpeech = speechTarget(speech, clock, -1)
  const nextSpeech = speechTarget(speech, clock, 1)
  const player = useArchivePlayer(
    segments,
    (utc) => {
      setClock(utc)
      const date = localDay(utc, timezoneRef.current)
      if (date !== dayRef.current) {
        dayRef.current = date
        setDay(date)
        setCalendarMonth(monthOf(date))
      }
    },
    setNotice,
    (before) => void previousAcrossDays(before),
    audioSegments,
  )
  const playerRef = useRef(player)
  playerRef.current = player

  useEffect(() => {
    setLoadingPrevious(false)
    return () => clipNavigation.current?.abort()
  }, [cameraId, day, apiBase])

  function cancelClipNavigation() {
    clipNavigation.current?.abort()
    clipNavigation.current = null
    setLoadingPrevious(false)
  }

  async function previousAcrossDays(before: number) {
    if (!camera) return
    cancelClipNavigation()
    const controller = new AbortController()
    clipNavigation.current = controller
    setLoadingPrevious(true)
    try {
      let date: string | null = localDay(before - 1, timezone)
      const firstDay = camera ? localDay(camera.first, timezone) : date
      while (date && date >= firstDay) {
        const data = await fetchCameraDay(
          apiBase,
          camera,
          date,
          controller.signal,
          archiveOnly,
        )
        if (controller.signal.aborted) return
        const previous = buildSegments(data.recordings, camera.sourceIds)
          .filter((s) => s.end <= before)
          .at(-1)
        if (previous) {
          const targetDay = localDay(previous.start, timezone)
          const autoplay = playerRef.current.getPlayIntent()
          if (targetDay === day) {
            playerRef.current.seek(previous.start, autoplay)
          } else {
            pending.current = {
              camera: cameraId,
              day: targetDay,
              utc: previous.start,
              autoplay,
            }
            playerRef.current.clear()
            setClock(previous.start)
            setDay(targetDay)
            setCalendarMonth(monthOf(targetDay))
          }
          return
        }
        date = data.previousDay && data.previousDay < date ? data.previousDay : null
      }
      setNotice('You’ve reached the beginning of the available footage.')
    } catch (error) {
      if (!controller.signal.aborted)
        setNotice(
          error instanceof Error
            ? error.message
            : 'Unable to load the previous recording. Try again.',
        )
    } finally {
      if (clipNavigation.current === controller) {
        clipNavigation.current = null
        setLoadingPrevious(false)
      }
    }
  }

  function skipDay(direction: -1 | 1) {
    if (!day || !camera) return
    cancelClipNavigation()
    const targetDay =
      direction === 1
        ? addDays(day, 1)
        : clockRef.current - dayStart < 60000
          ? addDays(day, -1)
          : day
    if (targetDay > today || targetDay < localDay(camera.first, timezone)) return
    const utc = targetDay === day ? dayStart : dayBounds(targetDay, timezone).start
    const autoplay = player.getPlayIntent()
    setCalendarOpen(false)
    if (targetDay === day && dayQuery.data?.recordings.length) {
      player.seek(utc, autoplay)
      return
    }
    pending.current = { camera: cameraId, day: targetDay, utc, autoplay }
    player.clear()
    setClock(utc)
    setDay(targetDay)
    setCalendarMonth(monthOf(targetDay))
  }

  useEffect(() => {
    const data = dayQuery.data,
      requested = pending.current
    if (
      !data ||
      !requested ||
      requested.camera !== cameraId ||
      requested.day !== day ||
      data.day !== day
    )
      return
    pending.current = null
    if (!data.recordings.length) {
      player.clear()
      setClock(data.start)
      return
    }
    player.seek(dayPlayhead(data, segments, requested.utc), requested.autoplay)
  }, [dayQuery.data, cameraId, day])

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(''), 5500)
    return () => clearTimeout(timer)
  }, [notice])

  useEffect(() => {
    if (cameraId && day && clock) {
      onPositionChange?.({ camera: cameraId, day, at: new Date(clock).toISOString() })
    }
  }, [cameraId, day, clock, onPositionChange])

  function activity() {
    setHud(true)
    if (hudTimer.current) clearTimeout(hudTimer.current)
    hudTimer.current = null
    // A held scrub/pinch is activity even when the finger stops moving.
    if (activePointers.current.size) return
    hudTimer.current = setTimeout(
      () => {
        if (
          !dockHovered.current &&
          !root.current?.querySelector('.bottom-dock :focus-visible')
        )
          setHud(false)
      },
      playerRef.current.playing ? (toolsExpandedRef.current ? 6000 : 3500) : 7000,
    )
  }
  useEffect(() => {
    function release(event: PointerEvent) {
      if (activePointers.current.delete(event.pointerId)) activity()
    }
    function blur() {
      activePointers.current.clear()
      activity()
    }
    // Listen outside the player too, for gestures released beyond its bounds.
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    window.addEventListener('lostpointercapture', release)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
      window.removeEventListener('lostpointercapture', release)
      window.removeEventListener('blur', blur)
    }
  }, [])
  useEffect(() => {
    const query = matchMedia(mobileLandscapeQuery)
    const update = () => setMobileLandscape(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  useEffect(() => {
    activity()
    return () => {
      if (hudTimer.current) clearTimeout(hudTimer.current)
    }
  }, [player.playing, mobileLandscape, toolsExpanded])
  function fullscreen() {
    activity()
    return toggleFullscreen()
  }
  function chooseDay(value: string) {
    cancelClipNavigation()
    if (value > localDay(Date.now(), timezone)) return
    setCalendarOpen(false)
    setCalendarMonth(monthOf(value))
    const utc = clockRef.current ? onDay(clockRef.current, value, timezone) : null
    if (value === day && dayQuery.data) {
      if (utc !== null && dayQuery.data.recordings.length)
        player.seek(dayPlayhead(dayQuery.data, segments, utc))
      return
    }
    pending.current = { camera: cameraId, day: value, utc, autoplay: player.playing }
    player.clear()
    setDay(value)
  }
  function chooseCamera(value: string) {
    cancelClipNavigation()
    const next = visibleCameras?.find((c) => c.id === value)
    if (!next) return
    const utc = Math.max(
      next.first,
      Math.min(next.last - 1, clockRef.current || next.first),
    )
    const date = localDay(utc, next.timezone)
    pending.current = { camera: value, day: date, utc, autoplay: player.playing }
    player.clear()
    setCameraId(value)
    setDay(date)
    setCalendarMonth(monthOf(date))
    setCalendarOpen(false)
  }
  useEffect(() => {
    if (keyboard === 'none') return
    const key = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      const target = e.target as HTMLElement
      if (
        target.closest(
          'a[href],button,input,textarea,select,[contenteditable],[role="slider"]',
        ) &&
        e.key !== 'Escape' &&
        !(e.code === 'Space' && target.matches('.time-ruler[role="slider"]'))
      )
        return
      if (e.key === 'Escape') {
        setCalendarOpen(false)
        return
      }
      if (e.code === 'Space') {
        e.preventDefault()
        playerRef.current.toggle()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        cancelClipNavigation()
        e.preventDefault()
        playerRef.current.seek(
          clockRef.current +
            (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 600000 : 60000),
        )
      } else if (e.key.toLowerCase() === 'm')
        playerRef.current.setMuted(!playerRef.current.muted)
      else if (e.key.toLowerCase() === 'f') void fullscreen()
      else if (e.key.toLowerCase() === 'c' && controls === 'full') {
        setCalendarOpen((open) => !open)
      }
      activity()
    }
    const element = keyboard === 'global' ? window : root.current
    element?.addEventListener('keydown', key as EventListener)
    return () => element?.removeEventListener('keydown', key as EventListener)
  }, [keyboard, controls])

  const data = dayQuery.data
  const showHud =
    hud ||
    (!player.playing && (!mobileLandscape || !player.recording)) ||
    calendarOpen ||
    cameraOpen ||
    Boolean(player.error)
  const empty = Boolean(data && !data.recordings.length)
  const busy = cameras.isPending || (dayQuery.isFetching && !data)
  const queryError = cameras.error ?? dayQuery.error
  const logoUrl = new URL('https://vod.ohn.sh/')
  setPresetForUrl(logoUrl, preset)
  if (archiveOnly) logoUrl.searchParams.set('archiveOnly', 'true')
  if (cameraId) logoUrl.searchParams.set('camera', cameraId)
  if (clock) setPlayerPosition(logoUrl, cameraId, clock)

  return (
    <div
      ref={root}
      data-capture-time={
        captureMode && player.slots[player.active]
          ? wallToMedia(
              player.slots[player.active]!.segment.recording,
              player.slots[player.active]!.utc,
            )
          : undefined
      }
      className={`archive-app controls-${controls} ${toolsExpanded ? 'tools-expanded' : 'tools-collapsed'} ${showHud ? 'hud-visible' : 'hud-hidden'} ${viewportFullscreen ? 'viewport-fullscreen' : ''} ${className}`}
      tabIndex={0}
      role="region"
      aria-label="VOD archive player"
      onPointerMove={(event) => {
        dockHovered.current =
          event.pointerType === 'mouse' &&
          !!(event.target as HTMLElement).closest('.bottom-dock')
        if ((event.target as HTMLElement).closest('.crop-drawer')) return
        if (event.pointerType === 'mouse' && event.buttons === 0) activity()
      }}
      onPointerLeave={() => {
        dockHovered.current = false
        activity()
      }}
      onPointerDownCapture={(event) => {
        // Angle selection is independent of the playback HUD, including the
        // window-level pointer-release activity tracking.
        if ((event.target as HTMLElement).closest('.crop-drawer')) {
          wakeGesture.current = false
          return
        }
        activePointers.current.add(event.pointerId)
        if (
          !(event.target as HTMLElement).closest(
            'a[href],button,input,select,[role="slider"]',
          )
        )
          root.current?.focus({ preventScroll: true })
        wakeGesture.current = controls !== 'none' && !showHud
        if (wakeGesture.current) {
          // Keep the entire reveal gesture off controls, including its final click.
          event.preventDefault()
          event.stopPropagation()
          event.currentTarget.setPointerCapture(event.pointerId)
        }
        activity()
      }}
      onClickCapture={(event) => {
        if (wakeGesture.current && event.detail > 0) {
          event.preventDefault()
          event.stopPropagation()
          wakeGesture.current = false
        }
      }}
      onFocusCapture={(event) => {
        if (!(event.target as HTMLElement).closest('.crop-drawer')) activity()
      }}
    >
      <div
        className="video-stage"
        onClick={controls === 'none' ? player.toggle : undefined}
        onDoubleClick={() => void fullscreen()}
      >
        {player.slots.map((slot, index) => (
          <VideoPlayer key={index}>
            <Container
              className={`video-slot ${index === player.active ? 'active' : ''}`}
              aria-hidden={index !== player.active}
              inert={index !== player.active}
            >
              <Video
                ref={(element) => {
                  player.videos.current[index] = element
                }}
                src={slot?.segment.recording.videoUrl}
                crossOrigin="anonymous"
                poster={slot?.segment.recording.posterUrl ?? undefined}
                playsInline
                muted={
                  index === player.active && !player.externalAudio ? player.muted : true
                }
                preload={index === player.active || player.warm ? 'auto' : 'metadata'}
                {...player.handlers(index)}
              />
            </Container>
          </VideoPlayer>
        ))}
        <audio
          ref={player.audio}
          src={player.audioSlot?.segment.recording.videoUrl}
          preload="auto"
          muted={player.muted}
          {...player.audioHandlers()}
        />
        {camera?.view === 'quad' && (
          <CropOverlay
            videos={player.videos}
            active={player.active}
            source={player.recording?.videoUrl ?? ''}
          />
        )}
        <div className="stage-shade" />
        <AudioVisualization
          visualization={visualization}
          amplitudeEnvelope={amplitudeEnvelope}
          videos={player.videos}
          active={player.active}
          enabled={camera?.view === 'audio' && !!player.recording}
          name={camera?.name ?? ''}
        />
        {player.slots.map((slot, index) => (
          <Captions
            key={index}
            videos={player.videos}
            index={index}
            recordingId={slot?.segment.recording.id}
            recording={slot?.segment.recording}
            segment={slot?.segment}
            audioSegments={audioSegments}
            apiBase={apiBase}
            active={index === player.active}
            enabled={captions}
          />
        ))}
      </div>

      {showLogo && (
        <div className="topbar hud">
          <a
            className="brand-link"
            href={logoUrl.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Watch on vod.ohn.sh at the current timestamp (opens in a new tab)"
          >
            <img
              className="brand-logo"
              src={brandLogo}
              alt="VOD"
              width="117"
              height="28"
            />
          </a>
        </div>
      )}

      {busy && (
        <div className="stage-message">
          <LoaderCircle className="spin" size={30} />
          <p>Opening the archive…</p>
        </div>
      )}
      {queryError && (
        <div className="stage-message error-panel">
          <h2>Unable to open the archive</h2>
          <p>{queryError.message}</p>
          <button
            className="primary"
            onClick={() => {
              void cameras.refetch()
              void dayQuery.refetch()
            }}
          >
            Try again
          </button>
        </div>
      )}
      {!busy && !queryError && cameras.data?.length === 0 && (
        <div className="stage-message">
          <h2>Your archive is empty</h2>
          <p>Import or ingest recordings with the cam CLI to get started.</p>
        </div>
      )}
      {!busy && !queryError && !!cameras.data?.length && visibleCameras?.length === 0 && (
        <div className="stage-message">
          <h2>No cameras in this preset</h2>
          <p>No available recordings match the selected locations.</p>
        </div>
      )}
      {empty && !busy && (
        <div className="stage-message">
          <CalendarDays size={30} />
          <h2>A quiet day in the archive</h2>
          <p>No indexed footage for this camera on {dateLabel(day)}.</p>
          <div className="empty-actions">
            {data?.previousDay && (
              <button onClick={() => chooseDay(data.previousDay!)}>
                Previous recorded day
              </button>
            )}
            {data?.nextDay && (
              <button
                disabled={data.nextDay > today}
                onClick={() => chooseDay(data.nextDay!)}
              >
                Next recorded day
              </button>
            )}
          </div>
        </div>
      )}
      {player.error && (
        <div className="stage-message error-panel">
          <h2>Playback unavailable</h2>
          <p>{player.error}</p>
          <div className="empty-actions">
            <button className="primary" onClick={player.retry}>
              Retry recording
            </button>
            <button onClick={player.next}>Next recording</button>
          </div>
        </div>
      )}
      {!player.error && player.buffering && !busy && player.recording && (
        <div className="buffer-indicator" aria-label="Buffering">
          <LoaderCircle className="spin" size={26} />
        </div>
      )}
      {controls !== 'none' &&
        !busy &&
        !player.error &&
        player.recording &&
        !player.playing &&
        !player.buffering && (
          <button
            className="center-play hud"
            aria-label="Play recording"
            onClick={player.toggle}
          >
            <Play size={30} fill="currentColor" />
          </button>
        )}

      <div className="notice" role="status" aria-live="polite">
        {notice}
      </div>

      {controls !== 'none' && (
        <PlayerFooter
          expanded={toolsExpanded}
          onToggleExpanded={() => {
            setToolsExpanded(!toolsExpanded)
            setCalendarOpen(false)
            setCameraOpen(false)
            activity()
          }}
          calendar={
            controls === 'full'
              ? {
                  month: calendarMonth || monthOf(day),
                  selected: day,
                  maxDay: today,
                  minDay: camera ? localDay(camera.first, camera.timezone) : undefined,
                  data: calendar.data,
                  loading: calendar.isFetching,
                  onMonth: setCalendarMonth,
                  onSelect: chooseDay,
                  onClose: () => setCalendarOpen(false),
                }
              : undefined
          }
          calendarOpen={calendarOpen}
          onCalendarOpenChange={setCalendarOpen}
          mobilePickers={mobilePickers}
          player={player}
          captions={captions}
          onToggleCaptions={toggleCaptions}
          navigateSpeech={navigateSpeech}
          onToggleSpeech={() => {
            cancelClipNavigation()
            setSpeechNavigationEnabled(!speechNavigationEnabled)
          }}
          previous={{
            onClick: () =>
              navigateSpeech
                ? previousSpeech && player.seek(previousSpeech.start)
                : player.previous(),
            disabled:
              !player.recording ||
              !data ||
              loadingPrevious ||
              (navigateSpeech && !previousSpeech),
          }}
          next={{
            onClick: () =>
              navigateSpeech
                ? nextSpeech && player.seek(nextSpeech.start)
                : player.next(),
            disabled:
              !player.recording ||
              !data ||
              loadingPrevious ||
              (navigateSpeech && !nextSpeech),
          }}
          fullscreen={expanded || viewportFullscreen}
          onFullscreen={() => void fullscreen()}
          timeline={
            data
              ? {
                  defaultZoom: latestHour ? '1h' : defaultZoom,
                  initialRange: initialRange ?? latestRange.current,
                  data,
                  events: eventQuery.data ?? [],
                  segments,
                  speech,
                  clock: clock || data.start,
                  buffered: player.buffered,
                  onSeek: (utc) => {
                    cancelClipNavigation()
                    player.seek(utc)
                  },
                }
              : undefined
          }
        >
          {controls === 'full' && (
            <ArchiveNavigation
              previousDay={{
                onClick: () => skipDay(-1),
                disabled:
                  !camera ||
                  !day ||
                  !data ||
                  loadingPrevious ||
                  (day <= localDay(camera.first, timezone) && clock - dayStart < 60000),
              }}
              nextDay={{
                onClick: () => skipDay(1),
                disabled: !camera || !day || day >= today || !data || loadingPrevious,
              }}
              calendarOpen={toolsExpanded && calendarOpen}
              onCalendarOpenChange={setCalendarOpen}
              calendar={{
                month: calendarMonth || monthOf(day),
                selected: day,
                maxDay: today,
                minDay: camera ? localDay(camera.first, camera.timezone) : undefined,
                data: calendar.data,
                loading: calendar.isFetching,
                onMonth: setCalendarMonth,
                onSelect: chooseDay,
                onClose: () => setCalendarOpen(false),
              }}
              cameraPicker={{
                cameras: visibleCameras,
                selected: cameraId,
                open: cameraOpen,
                mobile: mobilePickers,
                onOpenChange: setCameraOpen,
                onSelect: chooseCamera,
              }}
            />
          )}
        </PlayerFooter>
      )}
    </div>
  )
}
